/**
 * Prysm - Utility Functions & Constants
 * Helpers for frontend components
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { SignalCategory, SignalSentiment, AlertSeverity, EntityType } from '@/types';

// ============================================================================
// CLASSNAME UTILITIES
// ============================================================================

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  
  return then.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: now.getFullYear() !== then.getFullYear() ? 'numeric' : undefined
  });
}

export function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

// Category configuration
export const CATEGORY_CONFIG: Record<SignalCategory, { label: string; color: string; icon: string }> = {
  hiring: { label: 'Hiring', color: 'text-blue-600', icon: '👥' },
  funding: { label: 'Funding', color: 'text-green-600', icon: '💰' },
  product: { label: 'Product', color: 'text-purple-600', icon: '🚀' },
  policy: { label: 'Policy', color: 'text-yellow-600', icon: '⚖️' },
  risk: { label: 'Risk', color: 'text-red-600', icon: '⚠️' },
  geo: { label: 'Geographic', color: 'text-teal-600', icon: '🌍' },
  brand: { label: 'Brand', color: 'text-pink-600', icon: '✨' },
  other: { label: 'Other', color: 'text-gray-600', icon: '📋' }
};

export function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-red-600';
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
